import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  styled
} from '@mui/material';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import THEME from '../../../constants/theme';

// Style for back button text
const BackButtonText = styled(Typography)({
  color: THEME.COLORS.NEUTRAL_BLACK,
  fontWeight: 500,
  marginLeft: '8px',
  cursor: 'pointer',
  transition: 'color 0.2s ease-in-out',
  '&:hover': {
    color: THEME.COLORS.PRIMARY_BLUE
  }
});

// Style for company toggle buttons
const CompanyToggleButton = styled(ToggleButton)({
  padding: '8px 16px',
  borderRadius: '4px',
  fontSize: '1rem',
  fontWeight: 500,
  height: '100%',
  '&.Mui-selected': {
    backgroundColor: THEME.COLORS.PRIMARY_BLUE,
    color: THEME.COLORS.NEUTRAL_WHITE,
    '&:hover': {
      backgroundColor: THEME.COLORS.PRIMARY_BLUE,
      opacity: 0.9
    }
  }
});

/**
 * Enhanced GoBackButton component that can include a company toggle
 *
 * @param {Object} props - Component props
 * @param {string} props.backTo - URL to navigate back to
 * @param {string} props.backText - Text to display next to back arrow
 * @param {boolean} props.showCompanyToggle - Whether to show company toggle
 * @param {string} props.selectedCompany - Currently selected company
 * @param {Function} props.handleCompanyChange - Function to handle company change
 * @param {Array} props.companies - Array of company options [{ value: "mqcapital", label: "MarQ Capital" }, ...]
 * @returns {React.Component} - Enhanced back button component
 */
const EnhancedGoBackButton = ({
  backTo,
  backText,
  showCompanyToggle = false,
  selectedCompany,
  handleCompanyChange,
  companies = [
    { value: 'mqcapital', label: 'MarQ Capital' },
    { value: 'spinnaker', label: 'Spinnaker' }
  ]
}) => {
  return (
    <Box
      display='flex'
      justifyContent='space-between'
      alignItems='center'
      width='100%'
      marginBottom={3}
      marginTop={2}
    >
      {/* Back Button */}
      <Link
        to={backTo}
        style={{
          display: 'flex',
          alignItems: 'center',
          textDecoration: 'none'
        }}
      >
        <ArrowBackIcon
          sx={{
            fontSize: '2rem',
            color: THEME.COLORS.NEUTRAL_BLACK,
            '&:hover': {
              color: THEME.COLORS.PRIMARY_BLUE
            }
          }}
        />
        <BackButtonText variant='h5'>{backText}</BackButtonText>
      </Link>

      {/* Company Selector (optional) */}
      {showCompanyToggle && selectedCompany && handleCompanyChange && (
        <ToggleButtonGroup
          value={selectedCompany}
          exclusive
          onChange={handleCompanyChange}
          aria-label='company selection'
          size='medium'
        >
          {companies.map(company => (
            <CompanyToggleButton
              key={company.value}
              value={company.value}
              aria-label={company.label}
            >
              {company.label}
            </CompanyToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
    </Box>
  );
};

export default EnhancedGoBackButton;
